/**
 * Deep-link utilities for returning users from external payment browsers
 * back into the Cordova mobile wrapper.
 *
 * After completing payment at an external provider (e.g. PayPal), the browser
 * receives a server-side redirect (HTTP 302) back to the SPA domain. Chrome
 * and Safari do not trigger App Links / Universal Links for such redirects,
 * so the SPA loads in the browser instead of the app. These utilities detect
 * that situation and build platform-appropriate URLs to bounce the user back.
 */

import { isWeb } from './platform';

/**
 * Maps the SPA hostname to the corresponding Android package name.
 * Used to construct Android Intent URIs when `window.__appInfo` is
 * unavailable (i.e. outside the Cordova WebView).
 */
const HOSTNAME_PACKAGE_MAP: Record<string, string> = {
  'exchange.smoove.world': 'world.smoove.exchange',
  'smoove-exchange-dev.pages.dev': 'world.smoove.exchange.dev',
  'smoove-exchange-stg.pages.dev': 'world.smoove.exchange.stg',
};

/**
 * Resolves the Android package name for a given hostname.
 * @returns the package name, or `null` if the hostname is not recognised
 */
export const getAndroidPackageName = (hostname: string): string | null => {
  if (!hostname) return null;
  return HOSTNAME_PACKAGE_MAP[hostname] ?? null;
};

/**
 * Detects an Android mobile browser via user-agent sniffing.
 * Only used for the return-to-app interstitial — not for feature gating.
 */
export const isMobileAndroid = (): boolean => /android/i.test(navigator.userAgent);

/**
 * Detects an iOS mobile browser via user-agent sniffing.
 * Only used for the return-to-app interstitial — not for feature gating.
 *
 * Note: iPadOS 13+ reports a desktop (Macintosh) user agent by default, so
 * this will not match modern iPads. Adding `navigator.maxTouchPoints > 1`
 * would catch them but at the cost of false positives on touch-enabled
 * laptops. Since iPad users are unlikely to have the Cordova wrapper
 * installed, the current regex is an acceptable trade-off.
 */
export const isMobileIos = (): boolean => /iphone|ipad|ipod/i.test(navigator.userAgent);

/**
 * Builds an Android Intent URI that opens the associated app for the given
 * HTTPS URL. Falls back to the browser if the app is not installed.
 *
 * Inputs are expected to come from `window.location` (or be constructed from
 * the same source) and are therefore already percent-encoded. If raw
 * user input is ever passed directly, callers must encode it first to avoid
 * breaking the `#Intent;` delimiter.
 *
 * @see https://developer.chrome.com/docs/android/intents
 */
export const buildAndroidIntentUri = (
  hostname: string,
  pathname: string,
  search: string,
  packageName: string,
): string => {
  // strip leading '?' from search — the intent URI format appends it
  const query = search.startsWith('?') ? search.slice(1) : search;
  const fallback = `https://${hostname}${pathname}${search}`;

  return `intent://${hostname}${pathname}${query ? `?${query}` : ''}#Intent;scheme=https;package=${packageName};S.browser_fallback_url=${encodeURIComponent(fallback)};end`;
};

/**
 * Checks whether the given hostname belongs to a known app environment.
 * @returns true if the hostname is in the hostname-to-package map
 */
export const isKnownAppHostname = (hostname: string): boolean => hostname in HOSTNAME_PACKAGE_MAP;

/**
 * Builds a platform-appropriate URL that will return the user to the native app.
 *
 * - **Android**: returns an `intent://` URI (Chrome fires App Links for intent URIs
 *   even though it does not fire them for server-side 302 redirects).
 * - **iOS**: returns the target HTTPS URL. Safari triggers Universal Links
 *   when the user *taps* an `<a>` link to an associated domain — the tap gesture
 *   is the key difference vs. the automatic 302 redirect that failed.
 *
 * @param targetPath Optional path + search to use instead of the current
 *                   `window.location`. Useful on redirector pages that should
 *                   deep-link to the final destination (e.g. `/trades/42?fromPayment=success`)
 *                   rather than back to the redirector URL itself.
 * @returns the URL string, or `null` if the platform is not mobile or the
 *          hostname is not recognised.
 */
export const buildReturnToAppUrl = (targetPath?: string): string | null => {
  const { hostname } = window.location;

  // resolve the path + search to embed in the deep link
  let pathname: string;
  let search: string;
  if (targetPath) {
    const qIndex = targetPath.indexOf('?');
    pathname = qIndex === -1 ? targetPath : targetPath.slice(0, qIndex);
    search = qIndex === -1 ? '' : targetPath.slice(qIndex);
  } else {
    pathname = window.location.pathname;
    search = window.location.search;
  }

  if (isMobileAndroid()) {
    const packageName = getAndroidPackageName(hostname);
    if (!packageName) return null;
    return buildAndroidIntentUri(hostname, pathname, search, packageName);
  }

  if (isMobileIos()) {
    if (!isKnownAppHostname(hostname)) return null;
    return `https://${hostname}${pathname}${search}`;
  }

  return null;
};

/**
 * Determines whether the return-to-app interstitial should be shown.
 *
 * True when ALL of the following hold:
 * 1. Running in a web browser (not inside the Cordova WebView)
 * 2. The device is a mobile phone/tablet (Android or iOS)
 * 3. The current URL carries a payment signal (`?payment=`, `?fromPayment=`,
 *    or a `/payments/` path prefix)
 * 4. The hostname maps to a known mobile app package
 */
export const shouldShowReturnToAppInterstitial = (): boolean => {
  if (!isWeb()) return false;
  if (!isMobileAndroid() && !isMobileIos()) return false;

  // check that the hostname belongs to a known app
  if (!isKnownAppHostname(window.location.hostname)) return false;

  // detect payment signal in the URL
  const params = new URLSearchParams(window.location.search);
  const hasParam = params.has('payment') || params.has('fromPayment');
  const hasPath = window.location.pathname.startsWith('/payments/');

  return hasParam || hasPath;
};
